import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { newsApi, forumApi, listingsApi, resolveImageUrl } from '../lib/api';
import { HERO_IMAGE, getCrownVicImage, handleImageError } from '../lib/images';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import RaceWinsSlideshow from '../components/RaceWinsSlideshow';
import RacingIntelWidget from '../components/RacingIntelWidget';
import FeaturedInterviewCard from '../components/FeaturedInterviewCard';
import { Newspaper, MessageSquare, Car, ChevronRight, Clock, Eye, DollarSign, MapPin, ArrowRight, Mail, Phone } from 'lucide-react';

export default function HomePage() {
  const [news, setNews] = useState([]);
  const [threads, setThreads] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, threadsRes, listingsRes] = await Promise.all([
          newsApi.getAll(0, 4),
          forumApi.getThreads(null, 0, 5),
          listingsApi.getAll({ limit: 4 }),
        ]);
        setNews(newsRes.data);
        setThreads(threadsRes.data);
        setListings(listingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-400 tag">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative py-20 md:py-24 lg:min-h-[70vh] lg:flex lg:items-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-transparent"></div>
        <div className="relative container mx-auto px-4 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-7 xl:col-span-8">
              <p className="tag text-[#FF3B30] mb-4">Short Track Racing</p>
              <h1 className="font-heading font-black text-5xl md:text-7xl text-white mb-6 tracking-tight leading-tight">
                CROWN VIC<br />NATION
              </h1>
              <p className="text-gray-300 text-lg mb-8 max-w-lg">
                Your ultimate destination for Crown Victoria short track racing news, community, and race cars for sale across the United States.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/news">
                  <Button className="bg-[#FF3B30] hover:bg-[#E6352B] text-white px-8 py-6 text-lg" data-testid="hero-news-btn">
                    <Newspaper className="w-5 h-5 mr-2" />
                    Latest News
                  </Button>
                </Link>
                <Link to="/marketplace">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg" data-testid="hero-marketplace-btn">
                    <Car className="w-5 h-5 mr-2" />
                    Find a Race Car
                  </Button>
                </Link>
              </div>
            </div>

            {/* Featured Interview — right side on desktop, below hero on mobile */}
            <div className="lg:col-span-5 xl:col-span-4 max-w-md lg:max-w-none mx-auto w-full">
              <FeaturedInterviewCard />
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <p className="tag text-[#FF3B30] mb-2">Stay Updated</p>
              <h2 className="font-heading font-bold text-3xl text-white">Latest News</h2>
            </div>
            <Link to="/news">
              <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="view-all-news-btn">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Featured Article */}
            {news[0] && (
              <Link to={`/news/${news[0].id}`} className="md:col-span-8" data-testid="featured-news-card">
                <Card className="bg-[#141414] border-[#262626] overflow-hidden card-hover h-full">
                  <div className="relative h-64 md:h-80">
                    <img
                      src={resolveImageUrl(news[0].image_url) || getCrownVicImage(news[0].id)}
                      alt={news[0].title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://customer-assets.emergentagent.com/job_crown-vic-central/artifacts/fml311ba_341040780_3390030624586050_8184513587913407132_n.jpg'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="flex gap-2 mb-3">
                        {news[0].tags?.slice(0, 2).map((tag, i) => (
                          <span key={i} className="tag bg-[#FF3B30] text-white px-2 py-1 rounded-sm">{tag}</span>
                        ))}
                      </div>
                      <h3 className="font-heading font-bold text-2xl text-white mb-2">{news[0].title}</h3>
                      <p className="text-gray-400 text-sm line-clamp-2">{news[0].summary}</p>
                    </div>
                  </div>
                </Card>
              </Link>
            )}

            {/* Other Articles */}
            <div className="md:col-span-4 space-y-4">
              {news.slice(1, 4).map((article) => (
                <Link key={article.id} to={`/news/${article.id}`} data-testid={`news-card-${article.id}`}>
                  <Card className="bg-[#141414] border-[#262626] p-4 card-hover">
                    <div className="flex gap-4">
                      <img
                        src={resolveImageUrl(article.image_url) || getCrownVicImage(article.id)}
                        alt={article.title}
                        className="w-20 h-20 object-cover rounded flex-shrink-0"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => handleImageError(e, article.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-white text-sm line-clamp-2 mb-1">{article.title}</h4>
                        <p className="text-gray-500 text-xs flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(article.created_at)}
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Race Winners Slideshow Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="mb-6">
                <p className="tag text-[#FF3B30] mb-2">Victory Lane</p>
                <h2 className="font-heading font-bold text-3xl text-white">Race Winners</h2>
              </div>
              <RaceWinsSlideshow />
            </div>
            <div>
              <div className="mb-6">
                <p className="tag text-[#FF3B30] mb-2">Share Your Win</p>
                <h2 className="font-heading font-bold text-xl text-white">Got a Trophy?</h2>
              </div>
              <div className="bg-[#141414] rounded-lg border border-[#262626] p-6">
                <p className="text-gray-400 text-sm mb-4">
                  Won a race? Post your victory photos in the{' '}
                  <Link to="/forum?category=race-wins" className="text-[#FF3B30] hover:underline">Race Wins</Link>{' '}or{' '}
                  <Link to="/forum?category=results" className="text-[#FF3B30] hover:underline">Race Results</Link>{' '}
                  forum and they'll show up right here on the homepage!
                </p>
                <Link to="/forum/thread/7ee3a0e6-c88f-41fd-adea-cfc7c360c4d1">
                  <Button className="w-full bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="post-win-btn">
                    Post Your Win
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Racing Intel Widget (Latest Winners / Upcoming / New Tracks) */}
      <RacingIntelWidget />

      {/* Forum & Listings Split Section */}
      <section className="py-16 bg-[#141414]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Forum Preview */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="tag text-[#FF3B30] mb-2">Community</p>
                  <h2 className="font-heading font-bold text-2xl text-white">Latest Discussions</h2>
                </div>
                <Link to="/forum">
                  <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="view-forum-btn">
                    View Forum <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>

              <div className="space-y-1">
                {threads.map((thread) => (
                  <Link key={thread.id} to={`/forum/thread/${thread.id}`} data-testid={`thread-card-${thread.id}`}>
                    <div className="p-4 border-b border-[#262626] hover:bg-[#1F1F1F] transition-colors">
                      <h4 className="font-medium text-white text-sm mb-2 line-clamp-1">{thread.title}</h4>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{thread.author_name}</span>
                        <span className="flex items-center"><MessageSquare className="w-3 h-3 mr-1" />{thread.reply_count}</span>
                        <span className="flex items-center"><Eye className="w-3 h-3 mr-1" />{thread.view_count}</span>
                      </div>
                    </div>
                  </Link>
                ))}

                {threads.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No discussions yet. Be the first to post!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Listings Preview */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="tag text-[#FF3B30] mb-2">Marketplace</p>
                  <h2 className="font-heading font-bold text-2xl text-white">Cars For Sale</h2>
                </div>
                <Link to="/marketplace">
                  <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="view-listings-btn">
                    View All <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {listings.map((listing) => (
                  <Link key={listing.id} to={`/marketplace/${listing.id}`} data-testid={`listing-card-${listing.id}`}>
                    <Card className="bg-[#1F1F1F] border-[#262626] overflow-hidden card-hover">
                      <div className="relative h-32">
                        <img
                          src={listing.images?.[0] ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${listing.images[0]}` : getCrownVicImage(listing.id)}
                          alt={listing.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                        <span className="absolute top-2 right-2 tag bg-[#FF3B30] text-white px-2 py-1 rounded-sm">
                          For Sale
                        </span>
                      </div>
                      <div className="p-3">
                        <h4 className="font-medium text-white text-sm line-clamp-1 mb-1">{listing.title}</h4>
                        <p className="text-[#FF3B30] font-bold text-sm flex items-center">
                          <DollarSign className="w-3 h-3" />
                          {formatPrice(listing.price).replace('$', '')}
                        </p>
                        <p className="text-gray-500 text-xs flex items-center mt-1">
                          <MapPin className="w-3 h-3 mr-1" />
                          {listing.location}
                        </p>
                      </div>
                    </Card>
                  </Link>
                ))}

                {listings.length === 0 && (
                  <div className="col-span-2 text-center py-8 text-gray-500">
                    <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No cars for sale yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 text-center">
          <p className="tag text-[#FF3B30] mb-4">Join the Community</p>
          <h2 className="font-heading font-bold text-4xl text-white mb-6">Ready to Race?</h2>
          <p className="text-gray-400 max-w-xl mx-auto mb-8">
            Whether you're looking to buy a race car, share your build progress, or connect with fellow Crown Vic enthusiasts, we've got you covered.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button className="bg-[#FF3B30] hover:bg-[#E6352B] text-white px-8 py-6" data-testid="cta-signup-btn">
                Create Account
              </Button>
            </Link>
            <Link to="/forum">
              <Button variant="outline" className="border-[#262626] text-white hover:bg-[#262626] px-8 py-6" data-testid="cta-forum-btn">
                Browse Forum
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="py-16 bg-[#141414] border-t border-[#262626]" id="contact">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <p className="tag text-[#FF3B30] mb-4">Get In Touch</p>
            <h2 className="font-heading font-bold text-4xl text-white mb-6">Contact Us</h2>
            <p className="text-gray-400 mb-8">
              Have a question, want to share a story, or need help with the site? We'd love to hear from you.
            </p>
            <a 
              href="mailto:crownvicnation.com@gmail.com"
              className="inline-flex items-center gap-3 bg-[#1F1F1F] border border-[#262626] hover:border-[#FF3B30] rounded-lg px-8 py-5 transition-colors group"
              data-testid="contact-email-link"
            >
              <div className="w-12 h-12 rounded-full bg-[#FF3B30] flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-medium text-lg group-hover:text-[#FF3B30] transition-colors">crownvicnation.com@gmail.com</p>
                <p className="text-gray-500 text-sm">Send us an email anytime</p>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
